import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { UploadedFileItem } from '@ldt/features/orders/components/uploaded_file_item';
import { useLdtFileUploads } from '@ldt/features/orders/hooks/use_ldt_file_uploads';
import { CloudUpload } from 'src/components/icons/cloud_upload';
import {
  LdtOrderDetailFieldsFragment,
  UploadedFileCategory,
  UploadedFileStatus,
} from 'src/types';

interface CancelOrderUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirmCancel: () => void;
  order: LdtOrderDetailFieldsFragment;
  orderRepository: LdtOrderRepository;
}

/**
 * Dialog for Artera (CS/Sales) users to upload cancel order documents before canceling.
 * Matches Dart flow: "Upload Files and Cancel Order" with CANCEL ORDER button enabled
 * only when at least one file is uploaded.
 */
export const CancelOrderUploadDialog = ({
  open,
  onClose,
  onConfirmCancel,
  order,
  orderRepository,
}: CancelOrderUploadDialogProps) => {
  const {
    getFilesForCategory,
    handleFileSelected,
    handleFileRemoved,
    isFileDeleting,
  } = useLdtFileUploads({
    order,
    orderRepository,
  });

  const cancelDocFiles = getFilesForCategory(UploadedFileCategory.CancelDoc);
  const canCancel =
    cancelDocFiles.length > 0 &&
    cancelDocFiles.every((file) => file.status === UploadedFileStatus.Complete);

  const handleConfirm = useCallback(() => {
    onConfirmCancel();
    onClose();
  }, [onConfirmCancel, onClose]);

  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      for (const file of acceptedFiles) {
        handleFileSelected(file, UploadedFileCategory.CancelDoc);
      }
    },
    [handleFileSelected],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    noClick: false,
    noDrag: false,
    multiple: true,
    disabled: !open,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ textAlign: 'left' }}>
        Upload Files and Cancel Order
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box {...getRootProps()}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: (theme) =>
                  `1px dashed ${
                    isDragActive
                      ? theme.palette.text.primary
                      : theme.palette.primary.main
                  }`,
                borderRadius: '6px',
                cursor: 'pointer',
                height: '30vh',
                backgroundImage: 'none',
                '&:hover': {
                  borderColor: (theme) => theme.palette.text.primary,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.primary.dark
                      : theme.palette.primary.light,
                },
              }}
            >
              <input {...getInputProps()} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <CloudUpload
                  sx={{
                    mr: 2,
                    fontSize: (theme) => theme.typography.h3.fontSize,
                    color: (theme) => theme.palette.text.secondary,
                  }}
                />
                <Typography
                  variant='body1'
                  sx={{
                    color: 'text.primary',
                  }}
                >
                  Click or drag and drop to upload
                </Typography>
                <Typography
                  variant='subtitle2'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  pdf / png / jpg / jpeg types are supported
                </Typography>
              </Box>
            </Paper>
          </Box>

          {cancelDocFiles.length > 0 && (
            <Box
              sx={{
                maxHeight: 240,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {cancelDocFiles.map((file, index) => (
                <UploadedFileItem
                  key={file.id || index}
                  file={file}
                  onRemove={(fileId) => handleFileRemoved(fileId)}
                  index={index}
                  isRemoving={isFileDeleting?.(file.id)}
                />
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', gap: 2 }}>
        <Button variant='text' onClick={onClose} size='large'>
          CLOSE
        </Button>
        <Button
          variant='contained'
          onClick={handleConfirm}
          size='large'
          disabled={!canCancel}
        >
          CANCEL ORDER
        </Button>
      </DialogActions>
    </Dialog>
  );
};
